import React from "react";

export interface LegalDocumentLike {
  content?: string | null;
}

interface LegalDocumentBodyProps {
  doc: LegalDocumentLike | null;
  fallback?: React.ReactNode;
}

interface Section {
  number?: string;
  title?: string;
  paragraphs: string[];
}

/**
 * Matches a numbered clause heading such as `11. Limitation of Liability`.
 * The seeded legal documents store plain text with `N. Title` headings, blank
 * lines between paragraphs and single newlines after a heading.
 */
const HEADING_RE = /^(\d+)\.\s+(.*\S)$/;

const parseContent = (raw: string): Section[] => {
  const lines = raw.replace(/\r\n?/g, "\n").split("\n");
  const sections: Section[] = [];
  let current: Section = { paragraphs: [] };
  let buffer: string[] = [];

  const flushParagraph = () => {
    const text = buffer.join(" ").replace(/\s+/g, " ").trim();
    if (text) current.paragraphs.push(text);
    buffer = [];
  };

  const pushSection = () => {
    if (current.number || current.title || current.paragraphs.length) {
      sections.push(current);
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      continue;
    }
    const match = line.match(HEADING_RE);
    if (match) {
      flushParagraph();
      pushSection();
      current = { number: `${match[1]}.`, title: match[2], paragraphs: [] };
      continue;
    }
    buffer.push(line);
  }
  flushParagraph();
  pushSection();

  return sections;
};

/**
 * Renders a legal document's stored text with clause headings, paragraph
 * spacing and a readable measure. Falls back to `fallback` when there is no
 * document (e.g. the API is unreachable).
 */
const LegalDocumentBody: React.FC<LegalDocumentBodyProps> = ({ doc, fallback }) => {
  const content = doc?.content;
  if (!content) return <>{fallback}</>;

  const sections = parseContent(content);

  return (
    <div className="space-y-7 text-[15px] leading-7 text-gray-600">
      {sections.map((section, index) => (
        <section key={index} className="space-y-3">
          {section.title && (
            <h2 className="flex items-baseline gap-2 border-b border-gray-100 pb-2 text-base font-bold text-[#333]">
              {section.number && (
                <span className="shrink-0 text-sm font-extrabold text-[#ffa217]">
                  {section.number}
                </span>
              )}
              <span>{section.title}</span>
            </h2>
          )}
          {section.paragraphs.map((paragraph, pIndex) => (
            <p key={pIndex}>{paragraph}</p>
          ))}
        </section>
      ))}
    </div>
  );
};

export default LegalDocumentBody;
